// src/data/projects.js
// Per-project config for the case-study detail pages (rendered by src/Pages/ProjectPage.js).
// Each project carries its own bespoke theme (matching the Claude Design detail pages),
// copy, hero layout variant, and the real artwork available for it. Galleries are sized to
// real images only — no empty placeholders. Send higher-res per-project shots to enrich them.
import mixlabMascot from '../assets/portfolio/mixlab.jpg';
import mixlabBefore from '../assets/portfolio/mixlab-before.jpg';
import mixlabPoster from '../assets/portfolio/mixlab-poster.jpg';
import kojeBoard from '../assets/portfolio/koje.jpg';
import ambreBoard from '../assets/portfolio/ambre.jpg';
import dinesurf1 from '../assets/portfolio/dinesurf.jpg';
import dinesurf2 from '../assets/portfolio/dinesurf-2.jpg';
import dinesurf3 from '../assets/portfolio/dinesurf-3.jpg';
import dmMain from '../assets/portfolio/dinesurf-mastercard.jpg';
import dm2 from '../assets/portfolio/dinesurf-mc-2.jpg';
import social1 from '../assets/portfolio/social.jpg';
import social2 from '../assets/portfolio/social-2.jpg';
import social3 from '../assets/portfolio/social-3.jpg';
import social4 from '../assets/portfolio/social-4.jpg';
import social5 from '../assets/portfolio/social-5.jpg';
import beaubelle1 from '../assets/portfolio/beaubelle.jpg';

// Full portfolio order — drives the "0X / 07" numbering and the Next → chain.
export const ALL = [
  { slug: 'mix-lab', name: 'Mix Lab' },
  { slug: 'koje-bites', name: 'Koje Bites' },
  { slug: 'ambre-aromas', name: 'Ambré Aromas' },
  { slug: 'dinesurf', name: 'Dinesurf' },
  { slug: 'dinesurf-mastercard', name: 'Dinesurf × Mastercard' },
  { slug: 'beaubelle', name: 'Beaubelle' },
  { slug: 'social-marketing', name: 'Social & Marketing' },
];

export const PROJECTS = {
  // ---------------------------------------------------------------- 01 Mix Lab
  'mix-lab': {
    heroVariant: 'standard',
    theme: {
      bg: '#0B0B0C', ink: '#F2F2EF', body: '#B4B4B0', muted: '#7A7A7E',
      accent: '#C7F73E', accentText: '#0B0B0C', hair: 'rgba(255,255,255,.09)', barBg: 'rgba(11,11,12,.72)',
      titleFont: "'Space Grotesk', system-ui, sans-serif", titleWeight: 700,
      bodyFont: "'Space Grotesk', system-ui, sans-serif", mono: "'Space Mono', monospace",
      radius: 8, eyebrowLS: '.2em',
      glows: [
        { color: '#C7F73E', size: 640, o: 0.16, top: '-20%', right: '-10%' },
        { color: '#FF3D9A', size: 520, o: 0.14, bottom: '-10%', left: '-8%' },
      ],
      fonts: 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap',
    },
    eyebrow: 'Logo Redesign · Brand Refresh · 2025',
    title: [{ t: 'Mix' }, { t: '.', accent: true }, { t: 'Lab' }],
    titleSize: 'clamp(64px,13vw,190px)',
    intro: 'A neon-lit cocktail lab needed a mark as sharp as its pours. I rebuilt the identity from the logo up — electric, precise, unmistakable at a glance.',
    meta: [['Role', 'Brand & Logo'], ['Scope', 'Identity System'], ['Year', '2025']],
    briefHeading: 'Make the mark feel like the drink — electric, exact, a little dangerous.',
    brief: [
      'The original Mix Lab logo read as generic and got lost on a crowded shelf and a dim bar. The redesign leans into the “lab” idea: a precise, engineered wordmark with an acid-green signal colour that glows under neon and holds up shrunk to a bottle cap.',
      'From the mark I built the full kit — type scale, colour system, and a set of templates the team can run themselves across menus, bottles, and social.',
    ],
    images: { hero: mixlabPoster, before: mixlabBefore, after: mixlabMascot, gallery: [] },
    heroAspect: '16 / 8',
  },

  // -------------------------------------------------------------- 02 Koje Bites
  'koje-bites': {
    heroVariant: 'standard',
    theme: {
      bg: '#FBF6EE', ink: '#241B12', body: '#5C5043', muted: '#8A7B68',
      accent: '#E5482F', accentText: '#FBF6EE', hair: 'rgba(36,27,18,.1)', barBg: 'rgba(251,246,238,.8)',
      titleFont: "'DM Serif Display', Georgia, serif", titleWeight: 400,
      bodyFont: "'Manrope', system-ui, sans-serif", mono: null,
      radius: 22, eyebrowLS: '.2em', glows: [],
      fonts: 'https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Manrope:wght@400;500;600;700&display=swap',
    },
    eyebrow: 'Brand Identity · Creative Direction · 2024',
    title: [{ t: 'Koje ' }, { t: 'Bites', accent: true, italic: true }],
    titleSize: 'clamp(58px,12vw,176px)',
    intro: 'A homegrown food brand with big flavour and no visual voice. I gave Koje Bites a warm, appetite-first identity — bold colour, hand-warm type, and packaging that makes you hungry before you read a word.',
    meta: [['Role', 'Brand Identity'], ['Scope', 'Logo · Packaging'], ['Year', '2024']],
    briefHeading: 'Design it to taste as good as it looks.',
    brief: [
      'Koje Bites came with great food and inconsistent visuals — a different look on every post and package. I anchored the brand around a warm tomato-and-mustard palette, a confident serif logotype, and a flexible layout system.',
      'The result reads instantly as Koje across labels, menus and the feed — appetising, friendly, and unmistakably its own.',
    ],
    images: { hero: kojeBoard, gallery: [] },
    heroAspect: '16 / 8',
  },

  // ------------------------------------------------------------ 03 Ambré Aromas
  'ambre-aromas': {
    heroVariant: 'centered',
    theme: {
      bg: '#14100C', ink: '#F0E7DA', body: '#B5A991', muted: '#8A7658',
      accent: '#C79A5B', accentText: '#14100C', hair: 'rgba(240,231,218,.09)', barBg: 'rgba(20,16,12,.72)',
      titleFont: "'Cormorant Garamond', Georgia, serif", titleWeight: 500,
      bodyFont: "'Jost', system-ui, sans-serif", mono: null,
      radius: 10, eyebrowLS: '.4em',
      glows: [{ color: '#C79A5B', size: 760, o: 0.16, top: '-14%', left: '18%' }],
      fonts: 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=Jost:wght@300;400;500&display=swap',
    },
    eyebrow: 'Brand Identity · Creative · MMXXIV',
    title: [{ t: 'Ambré ' }, { t: 'Aromas', accent: true, italic: true }],
    titleSize: 'clamp(58px,12vw,168px)',
    intro: 'An identity as layered as a fragrance — warm, quiet, and expensive without saying so.',
    introSerif: true,
    meta: null,
    briefHeading: 'Build a house of scent that feels like a keepsake.',
    brief: [
      'Ambré Aromas needed to move from a maker’s project to a considered brand. I designed a restrained amber-and-cream system, an elegant serif wordmark, and a set of textures and layouts that let the product breathe.',
      'Every touchpoint — from label to unboxing to the grid — was tuned for a slow, sensory kind of luxury.',
    ],
    images: { hero: ambreBoard, gallery: [] },
    heroAspect: '16 / 8', heroPosition: 'top',
  },

  // --------------------------------------------------------------- 04 Dinesurf
  'dinesurf': {
    heroVariant: 'split',
    theme: {
      bg: '#F6F8FA', ink: '#0E1B2B', body: '#4B5A6B', muted: '#64748B',
      accent: '#FF5A5F', accentText: '#FFFFFF', hair: 'rgba(14,27,43,.08)', barBg: 'rgba(246,248,250,.82)',
      titleFont: "'Sora', system-ui, sans-serif", titleWeight: 800,
      bodyFont: "'Manrope', system-ui, sans-serif", mono: null,
      radius: 18, eyebrowLS: '.16em', glows: [],
      fonts: 'https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Manrope:wght@400;500;600;700&display=swap',
    },
    eyebrow: 'Corporate Campaign · 2025', eyebrowColor: '#12B5A5', eyebrowDot: true,
    title: [{ t: 'Dinesurf' }],
    titleSize: 'clamp(46px,7vw,96px)',
    intro: 'A dining-reservation platform ready to scale needed a campaign that felt as effortless as booking a table. I built a bright, confident visual system for launch across web, social and out-of-home.',
    meta: [['Role', 'Campaign Design'], ['Scope', 'Cross-platform'], ['Year', '2025']],
    briefHeading: 'Make booking a table feel like the easiest yes of the day.',
    brief: [
      'Dinesurf’s brief was clarity at scale: one campaign that could flex from a phone screen to a billboard without losing energy. I paired a coral-and-teal palette with the geometric Sora typeface and a clean grid built for speed.',
      'The kit shipped as ready-to-run templates so the marketing team could keep the campaign moving on their own.',
    ],
    images: {
      hero: dinesurf1,
      gallery: [
        { src: dinesurf2, alt: 'Dinesurf online-ordering campaign flyer' },
        { src: dinesurf3, alt: 'Dinesurf free-website campaign flyer' },
      ],
    },
    heroAspect: '4 / 5',
  },

  // ------------------------------------------------- 05 Dinesurf × Mastercard
  'dinesurf-mastercard': {
    heroVariant: 'standard',
    theme: {
      bg: '#0C0F14', ink: '#F5F5F5', body: '#B7BDC6', muted: '#8A93A0',
      accent: '#F79E1B', accentText: '#0C0F14', hair: 'rgba(255,255,255,.08)', barBg: 'rgba(12,15,20,.74)',
      titleFont: "'Sora', system-ui, sans-serif", titleWeight: 800,
      bodyFont: "'Manrope', system-ui, sans-serif", mono: null,
      radius: 18, eyebrowLS: '.2em', titleGradient: 'linear-gradient(90deg,#EB001B,#F79E1B)',
      glows: [
        { color: '#EB001B', size: 760, o: 0.2, top: '-10%', left: '-5%' },
        { color: '#F79E1B', size: 700, o: 0.2, bottom: '-20%', right: '-8%' },
      ],
      fonts: 'https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Manrope:wght@400;500;600;700&display=swap',
    },
    eyebrow: 'Collaborative Campaign · 2025',
    title: [{ t: 'Dinesurf ' }, { t: '× Mastercard', gradient: true }],
    titleSize: 'clamp(40px,7.5vw,120px)',
    intro: 'A co-branded dining campaign built to make two brands feel like one seamless offer. The design threads Dinesurf’s booking energy through a warm, premium palette for a partnership that reads as a genuine perk, not a bolt-on.',
    meta: [['Role', 'Campaign Design'], ['Scope', 'Co-branded rollout'], ['Year', '2025']],
    briefHeading: 'Two brands, one offer, zero friction.',
    brief: [
      'Partnership campaigns live or die on balance — neither brand should feel like a guest. I designed a shared visual language: a warm red-to-amber gradient, a confident co-lockup, and a modular layout that keeps both identities in step across every placement.',
      'The system carried the promotion cleanly across social, in-app, email and print — premium, consistent, and unmistakably a team-up.',
    ],
    images: {
      hero: dmMain,
      gallery: [{ src: dm2, alt: 'Dinesurf Elite Dining Access campaign visual' }],
    },
    heroAspect: '16 / 8',
  },

  // --------------------------------------------------------------- 06 Beaubelle
  'beaubelle': {
    heroVariant: 'editorial',
    theme: {
      bg: '#FFFFFF', ink: '#0A0A0A', body: '#4A4A4A', muted: '#8A8A8A',
      accent: '#B26E6E', accentText: '#FFFFFF', hair: 'rgba(10,10,10,.12)', barBg: 'rgba(255,255,255,.86)',
      titleFont: "'Bodoni Moda', Georgia, serif", titleWeight: 500,
      bodyFont: "'Jost', system-ui, sans-serif", mono: null,
      radius: 0, eyebrowLS: '.28em', glows: [],
      fonts: 'https://fonts.googleapis.com/css2?family=Bodoni+Moda:ital,opsz,wght@0,6..96,400;0,6..96,500;0,6..96,700;1,6..96,400&family=Jost:wght@300;400;500&display=swap',
    },
    editorialLabels: ['Fashion Brand Marketing', 'Lookbook / Social / Campaign — 2024'],
    title: [{ t: 'Beau' }, { t: 'belle', italic: true }],
    titleSize: 'clamp(64px,16vw,240px)',
    intro: 'A fashion label with strong product and soft presence. I gave Beaubelle an editorial voice — high contrast, generous space, and a marketing rhythm borrowed from the magazines.',
    introSerif: true,
    meta: null,
    briefLabel: 'The Brief — 01',
    briefHeading: 'Dress the marketing like the clothes.',
    brief: [
      'Beaubelle’s feed didn’t match the quality of the product. I built an editorial marketing system — a Bodoni display voice, a disciplined black-and-white base warmed by a single blush accent, and layouts with room to breathe.',
      'The look now carries from lookbook to launch post to paid campaign, elevated and consistent across every frame.',
    ],
    images: { hero: beaubelle1, gallery: [] },
    heroAspect: '16 / 9', heroPosition: 'top',
  },

  // ------------------------------------------------------ 07 Social & Marketing
  'social-marketing': {
    heroVariant: 'social',
    theme: {
      bg: '#FFFCF6', ink: '#171514', body: '#5C564E', muted: '#9A938A',
      accent: '#FF5E5B', accentText: '#FFFFFF', hair: 'rgba(23,21,20,.08)', barBg: 'rgba(255,252,246,.84)',
      titleFont: "'Poppins', system-ui, sans-serif", titleWeight: 800,
      bodyFont: "'Manrope', system-ui, sans-serif", mono: null,
      radius: 24, eyebrowLS: '.16em', glows: [],
      fonts: 'https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700;800&family=Manrope:wght@400;500;600;700&display=swap',
    },
    eyebrow: 'Social & Marketing',
    tags: [
      { label: 'PopTails', bg: '#FF5E5B' },
      { label: 'Mix Lab', bg: '#00C2A8' },
      { label: 'Akanlacha Delicacy', bg: '#7C4DFF' },
    ],
    title: [{ t: 'Social that ' }, { t: 'stops', color: '#FF5E5B' }, { t: ' the ' }, { t: 'scroll', color: '#00C2A8' }, { t: '.' }],
    titleSize: 'clamp(48px,9vw,140px)',
    intro: 'Ongoing content and marketing design across three very different brands — cocktails, nightlife, and food. One job every time: earn the double-tap and move the product.',
    meta: [['Role', 'Social & Marketing'], ['Scope', 'Content · Ads · Promo'], ['Year', '2023 — 25']],
    briefHeading: 'Three brands, three voices, one standard.',
    brief: [
      'Each brand needed its own energy — PopTails poppy and playful, Mix Lab electric, Akanlacha warm and appetising — without me starting from scratch every week. I built flexible templates and content systems for each, so the feeds stay on-brand and fast to produce.',
      'The work spans posts, carousels, story templates, promo graphics and ad creatives — consistent, distinct, and built to convert.',
    ],
    images: {
      hero: null,
      gallery: [
        { src: social1, alt: 'PopTails social post', tag: { label: 'PopTails', bg: '#FF5E5B' } },
        { src: social3, alt: 'Mix Lab relaunch poster', tag: { label: 'Mix Lab', bg: '#00C2A8' } },
        { src: social2, alt: 'Akanlacha Valentine flyer', tag: { label: 'Akanlacha', bg: '#7C4DFF' } },
        { src: social4, alt: 'Children’s Day seasonal post', tag: { label: 'Seasonal', bg: '#FFC23C', fg: '#171514' } },
        { src: social5, alt: 'February welcome post', tag: { label: 'Seasonal', bg: '#FFC23C', fg: '#171514' } },
      ],
    },
    galleryLabel: 'Selected content',
  },
};
